import { createContext, useContext } from "react";
import { Skeleton } from "../ui/skeleton";
import { cn } from "@/lib/utils";

const SkeletonRoleContext = createContext<"user" | "assistant">("assistant");

const SkeletonGroup = ({
  children,
  role = "assistant",
}: {
  children: React.ReactNode;
  role?: "user" | "assistant";
}) => {
  return (
    <SkeletonRoleContext.Provider value={role}>
      <div
        className={cn("flex flex-col", role === "user" ? " gap-4" : " gap-3")}
      >
        {children}
      </div>
    </SkeletonRoleContext.Provider>
  );
};

const SkeletonLine = ({ children }: { children?: React.ReactNode }) => {
  const role = useContext(SkeletonRoleContext);
  return (
    <div
      className={cn(
        "flex w-full",
        role === "user"
          ? "justify-start flex-row-reverse gap-2"
          : "justify-start flex-row gap-1",
      )}
    >
      {children}
    </div>
  );
};

export function ChatSkeleton() {
  return (
    <div className="flex flex-col gap-18">
      <SkeletonGroup role="user">
        <SkeletonLine>
          <Skeleton className="h-8 w-[40%] rounded-2xl" />
        </SkeletonLine>
      </SkeletonGroup>

      <SkeletonGroup role="assistant">
        <SkeletonLine>
          <Skeleton className="h-4 w-[30%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[20%]" />
          <Skeleton className="h-4 w-[40%]" />
          <Skeleton className="h-4 w-[20%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[15%]" />
          <Skeleton className="h-4 w-[40%]" />
          <Skeleton className="h-4 w-[30%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[8%]" />
          <Skeleton className="h-4 w-[12%]" />
          <Skeleton className="h-4 w-[30%]" />
          <Skeleton className="h-4 w-[20%]" />
          <Skeleton className="h-4 w-[20%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[20%]" />
          <Skeleton className="h-4 w-[8%]" />
          <Skeleton className="h-4 w-[20%]" />
          <Skeleton className="h-4 w-[12%]" />
          <Skeleton className="h-4 w-[30%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[50%]" />
          <Skeleton className="h-4 w-[28%]" />
          <Skeleton className="h-4 w-[20%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[20%]" />
          <Skeleton className="h-4 w-[6%]" />
          <Skeleton className="h-4 w-[18%]" />
          <Skeleton className="h-4 w-[12%]" />
          <Skeleton className="h-4 w-[32%]" />
        </SkeletonLine>
      </SkeletonGroup>

      <SkeletonGroup role="user">
        <SkeletonLine>
          <Skeleton className="h-8 w-[60%] rounded-2xl" />
        </SkeletonLine>
      </SkeletonGroup>

      <SkeletonGroup role="assistant">
        <SkeletonLine>
          <Skeleton className="h-4 w-[20%]" />
          <Skeleton className="h-4 w-[50%]" />
          <Skeleton className="h-4 w-[10%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[45%]" />
          <Skeleton className="h-4 w-[30%]" />
          <Skeleton className="h-4 w-[10%]" />
          <Skeleton className="h-4 w-[10%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[15%]" />
          <Skeleton className="h-4 w-[10%]" />
          <Skeleton className="h-4 w-[10%]" />
          <Skeleton className="h-4 w-[20%]" />
          <Skeleton className="h-4 w-[30%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[24%]" />
          <Skeleton className="h-4 w-[8%]" />
          <Skeleton className="h-4 w-[14%]" />
          <Skeleton className="h-4 w-[40%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[35%]" />
          <Skeleton className="h-4 w-[21%]" />
        </SkeletonLine>
        <SkeletonLine />
        <SkeletonLine />
        <SkeletonLine>
          <Skeleton className="h-4 w-[16%]" />
          <Skeleton className="h-4 w-[12%]" />
          <Skeleton className="h-4 w-[9%]" />
          <Skeleton className="h-4 w-[27%]" />
          <Skeleton className="h-4 w-[19%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[50%]" />
          <Skeleton className="h-4 w-[38%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[13%]" />
          <Skeleton className="h-4 w-[29%]" />
          <Skeleton className="h-4 w-[33%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[10%]" />
          <Skeleton className="h-4 w-[14%]" />
          <Skeleton className="h-4 w-[11%]" />
          <Skeleton className="h-4 w-[22%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[20%]" />
          <Skeleton className="h-4 w-[18%]" />
          <Skeleton className="h-4 w-[24%]" />
          <Skeleton className="h-4 w-[5%]" />
        </SkeletonLine>
        <SkeletonLine>
          <Skeleton className="h-4 w-[13%]" />
          <Skeleton className="h-4 w-[29%]" />
          <Skeleton className="h-4 w-[33%]" />
        </SkeletonLine>
        <SkeletonLine />
        <SkeletonLine />
        <SkeletonLine>
          <Skeleton className="h-4 w-[13%]" />
          <Skeleton className="h-4 w-[23%]" />
          <Skeleton className="h-4 w-[6%]" />
        </SkeletonLine>
      </SkeletonGroup>
    </div>
  );
}
